if (!customElements.get('ingredient-carousel-v2')) {
  class IngredientCarouselV2 extends HTMLElement {
    /** @type {AbortController | null} */
    #abort = null;

    /** @type {number} */
    #frame = 0;

    /** @type {number} */
    #settleTimer = 0;

    /** @type {number} */
    #activeIndex = -1;

    /** @type {number} */
    #position = -1;

    /** @type {HTMLElement | null} */
    #trackElement = null;

    /** @type {HTMLElement[]} */
    #slideElements = [];

    /** @type {number} */
    #slideCount = 0;

    connectedCallback() {
      const trackElement = this.querySelector('[data-carousel-track]');
      if (!(trackElement instanceof HTMLElement)) return;

      const realSlides = Array.from(trackElement.querySelectorAll('[data-carousel-slide]')).filter(
        /** @param {Element} slideElement */ (slideElement) => slideElement instanceof HTMLElement
      );
      this.#slideCount = realSlides.length;
      if (this.#slideCount === 0) return;

      this.#trackElement = trackElement;
      this.#slideElements = this.#slideCount > 1 ? this.#addClones(trackElement, realSlides) : realSlides;

      this.#abort = new AbortController();
      const { signal } = this.#abort;

      const startIndex = Number(this.dataset.startIndex) || 0;
      this.#scrollToPosition(this.#offset + startIndex, 'instant');
      this.#setPosition(this.#offset + startIndex);

      trackElement.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
      trackElement.addEventListener('scrollend', () => this.#settle(), { signal });

      this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.#step(1), { signal });

      this.querySelectorAll('[data-carousel-dot]').forEach(
        /** @param {Element} dotButton @param {number} dotIndex */ (dotButton, dotIndex) => {
          dotButton.addEventListener('click', () => this.#scrollToPosition(this.#offset + dotIndex), { signal });
        }
      );

      this.#slideElements.forEach(
        /** @param {HTMLElement} slideElement @param {number} slidePosition */ (slideElement, slidePosition) => {
          slideElement.addEventListener(
            'click',
            () => {
              if (slidePosition !== this.#position) this.#scrollToPosition(slidePosition);
            },
            { signal }
          );
        }
      );
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#abort = null;
      cancelAnimationFrame(this.#frame);
      clearTimeout(this.#settleTimer);
      this.querySelectorAll('[data-carousel-clone]').forEach(
        /** @param {Element} cloneElement */ (cloneElement) => cloneElement.remove()
      );
    }

    get #offset() {
      return this.#slideCount > 1 ? this.#slideCount : 0;
    }

    /** @param {HTMLElement} trackElement @param {HTMLElement[]} realSlides */
    #addClones(trackElement, realSlides) {
      /** @param {HTMLElement} slideElement */
      const createClone = (slideElement) => {
        const cloneElement = /** @type {HTMLElement} */ (slideElement.cloneNode(true));
        cloneElement.classList.remove('is-active');
        cloneElement.removeAttribute('data-shopify-editor-block');
        cloneElement.setAttribute('aria-hidden', 'true');
        cloneElement.setAttribute('data-carousel-clone', '');
        cloneElement.inert = true;
        return cloneElement;
      };

      const leadingClones = realSlides.map(createClone);
      const trailingClones = realSlides.map(createClone);
      trackElement.prepend(...leadingClones);
      trackElement.append(...trailingClones);

      return [...leadingClones, ...realSlides, ...trailingClones];
    }

    /** @param {number} direction */
    #step(direction) {
      this.#scrollToPosition(this.#position + direction);
    }

    /** @param {number} position @param {ScrollBehavior} [behavior] */
    #scrollToPosition(position, behavior = 'smooth') {
      const trackElement = this.#trackElement;
      const slideElement = this.#slideElements[position];
      if (!trackElement || !slideElement) return;

      const left = slideElement.offsetLeft - (trackElement.clientWidth - slideElement.offsetWidth) / 2;
      trackElement.scrollTo({ left, behavior });
    }

    #onScroll() {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        const trackElement = this.#trackElement;
        if (!trackElement) return;

        const center = trackElement.scrollLeft + trackElement.clientWidth / 2;
        let closestPosition = 0;
        let closestDistance = Infinity;
        this.#slideElements.forEach(
          /** @param {HTMLElement} slideElement @param {number} slidePosition */ (slideElement, slidePosition) => {
            const distance = Math.abs(slideElement.offsetLeft + slideElement.offsetWidth / 2 - center);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestPosition = slidePosition;
            }
          }
        );
        this.#setPosition(closestPosition);
      });

      if (!('onscrollend' in window)) {
        clearTimeout(this.#settleTimer);
        this.#settleTimer = window.setTimeout(() => this.#settle(), 150);
      }
    }

    #settle() {
      if (this.#offset === 0) return;
      const realPosition = this.#offset + this.#activeIndex;
      if (realPosition === this.#position) return;

      const trackElement = this.#trackElement;
      trackElement?.classList.add('is-jumping');
      this.#scrollToPosition(realPosition, 'instant');
      this.#setPosition(realPosition);
      requestAnimationFrame(() => trackElement?.classList.remove('is-jumping'));
    }

    /** @param {number} position */
    #setPosition(position) {
      if (position === this.#position) return;
      this.#position = position;

      this.#slideElements.forEach(
        /** @param {HTMLElement} slideElement @param {number} slidePosition */ (slideElement, slidePosition) => {
          slideElement.classList.toggle('is-active', slidePosition === position);
        }
      );

      const index = position % this.#slideCount;
      if (index === this.#activeIndex) return;
      this.#activeIndex = index;

      this.querySelectorAll('[data-carousel-info]').forEach(
        /** @param {Element} infoElement @param {number} infoIndex */ (infoElement, infoIndex) => {
          if (infoElement instanceof HTMLElement) infoElement.hidden = infoIndex !== index;
        }
      );

      this.querySelectorAll('[data-carousel-dot]').forEach(
        /** @param {Element} dotButton @param {number} dotIndex */ (dotButton, dotIndex) => {
          if (dotIndex === index) {
            dotButton.setAttribute('aria-current', 'true');
          } else {
            dotButton.removeAttribute('aria-current');
          }
        }
      );
    }
  }

  customElements.define('ingredient-carousel-v2', IngredientCarouselV2);
}
